// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import { useState, useEffect, useCallback } from "react";
import tenantService from "../services/tenantService";

/**
 * Custom hook for managing tenants data
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeAll - Include "All Tenants" option (default: true)
 * @param {string} options.allLabel - Label for "All" option (default: "All Tenants")
 * @param {boolean} options.autoFetch - Automatically fetch tenants on mount (default: true)
 * @returns {Object} - Hook return object with tenants data and methods
 */
const useTenants = (options = {}) => {
  const {
    includeAll = true,
    allLabel = "All Tenants",
    autoFetch = true,
    ...tenantQueryOptions
  } = options;

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(
    includeAll ? "all" : null,
  );

  /**
   * Fetch tenants from API.
   *
   * Uses a functional setter for `selectedTenant` so the callback does not
   * need `selectedTenant` as a dependency — otherwise every time we set the
   * default selection, this callback's reference changes and the mount-time
   * useEffect fires again, causing duplicate API calls down the chain.
   */
  const fetchTenants = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const tenantsData = await tenantService.getAllTenantsForFilter({
        includeAll,
        allLabel,
        ...tenantQueryOptions,
      });
      setTenants(tenantsData);

      // Set default selection only if nothing is selected yet.
      setSelectedTenant((current) => {
        if (!current && tenantsData.length > 0) return tenantsData[0].id;
        return current;
      });
    } catch (err) {
      console.error("Error fetching tenants:", err);
      setError("Failed to load tenants");
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, [includeAll, allLabel]);

  /**
   * Refresh tenants data
   */
  const refreshTenants = useCallback(() => {
    fetchTenants();
  }, [fetchTenants]);

  /**
   * Get tenant by ID
   */
  const getTenantById = useCallback(
    (tenantId) => {
      return tenants.find((tenant) => tenant.id === tenantId);
    },
    [tenants],
  );

  /**
   * Get selected tenant object
   */
  const getSelectedTenant = useCallback(() => {
    return getTenantById(selectedTenant);
  }, [selectedTenant, getTenantById]);

  /**
   * Check if "All Tenants" is selected
   */
  const isAllTenantsSelected = useCallback(() => {
    return selectedTenant === "all" || selectedTenant === "All Tenants";
  }, [selectedTenant]);

  /**
   * Get tenant name by ID
   */
  const getTenantName = useCallback(
    (tenantId) => {
      const tenant = getTenantById(tenantId);
      return tenant ? tenant.name : "Unknown Tenant";
    },
    [getTenantById],
  );

  /**
   * Get tenant options for select dropdowns
   */
  const getTenantOptions = useCallback(() => {
    return tenants.map((tenant) => ({
      value: tenant.id,
      label: tenant.name,
      subdomain: tenant.subdomain,
      status: tenant.status,
    }));
  }, [tenants]);

  // Auto-fetch tenants on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetchTenants();
    }
  }, [autoFetch, fetchTenants]);

  return {
    // Data
    tenants,
    selectedTenant,
    loading,
    error,

    // Methods
    setSelectedTenant,
    fetchTenants,
    refreshTenants,
    getTenantById,
    getSelectedTenant,
    getTenantName,
    getTenantOptions,
    isAllTenantsSelected,

    // Computed values
    tenantOptions: getTenantOptions(),
    selectedTenantName: getTenantName(selectedTenant),
    selectedTenantObject: getSelectedTenant(),
  };
};

export default useTenants;
